import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import ContactSection from "../components/ContactSection";

const Industries = () => {

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const [activeIndustry, setActiveIndustry] = useState(0);

    const industries = [
        {
            id: '01',
            title: 'Financial Services & Banking',
            shortDesc: 'Secure, resilient infrastructure for regulated environments',
            description: 'Financial institutions demand the highest levels of security, availability, and regulatory compliance from their IT environments. Solaris designs and manages infrastructure that meets stringent financial sector requirements - from core banking connectivity and data centre resilience to cybersecurity frameworks aligned with RBI, SEBI, and global compliance mandates.',
            image: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=900&auto=format&fit=crop&q=80',
            capabilities: ['Zero-downtime networking', 'Regulatory compliance frameworks', 'Secure data centre operations', 'Multi-site disaster recovery'],
            accent: '#2563eb'
        },
        {
            id: '02',
            title: 'Healthcare & Life Sciences',
            shortDesc: 'Connected, compliant IT for patient-critical operations',
            description: 'Healthcare organisations require IT infrastructure that is always available, always secure, and fully compliant with data protection regulations. Solaris delivers managed networking, secure cloud connectivity, and infrastructure monitoring solutions that support clinical workflows, protect patient data, and enable healthcare providers to focus on care delivery.',
            image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=900&auto=format&fit=crop&q=80',
            capabilities: ['Clinical network connectivity', 'HIPAA-aligned security controls', 'Managed infrastructure support', 'Business continuity planning'],
            accent: '#10b981'
        },
        {
            id: '03',
            title: 'Manufacturing & Industry 4.0',
            shortDesc: 'Operational technology networks built for the factory floor',
            description: 'Modern manufacturing depends on converged IT and OT environments that connect production systems, logistics platforms, and enterprise applications seamlessly. Solaris architects and manages industrial networking, edge computing, and secure connectivity solutions that support smart factory initiatives without compromising operational reliability.',
            image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?w=900&auto=format&fit=crop&q=80',
            capabilities: ['IT/OT network convergence', 'Edge compute solutions', 'Industrial wireless deployments', 'Supply chain connectivity'],
            accent: '#f59e0b'
        },
        {
            id: '04',
            title: 'Retail & E-Commerce',
            shortDesc: 'High-availability networks for seamless customer experiences',
            description: 'Retail businesses require resilient, high-performance infrastructure that delivers consistent experiences across physical stores, warehouses, and digital channels. Solaris provides managed networking, cloud connectivity, and security solutions that keep retail operations running reliably - whether during peak trading periods or everyday operations.',
            image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=900&auto=format&fit=crop&q=80',
            capabilities: ['Multi-site retail networking', 'POS & payment security', 'Cloud connectivity & SD-WAN', 'Loss prevention & surveillance networks'],
            accent: '#6366f1'
        },
        {
            id: '05',
            title: 'Education & Research',
            shortDesc: 'Scalable campus networks enabling modern learning',
            description: 'Educational institutions require robust, secure, and scalable network infrastructure that supports high-density wireless environments, digital learning platforms, and research computing. Solaris designs and manages campus networks, cybersecurity frameworks, and cloud connectivity solutions that empower students, faculty, and researchers to work without boundaries.',
            image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=900&auto=format&fit=crop&q=80',
            capabilities: ['High-density campus wireless', 'Research network infrastructure', 'Student data security', 'E-learning platform connectivity'],
            accent: '#0ea5e9'
        },
        {
            id: '06',
            title: 'Government & Public Sector',
            shortDesc: 'Secure, sovereign IT infrastructure for public institutions',
            description: 'Government agencies and public sector organisations operate under strict security, compliance, and procurement requirements. Solaris delivers enterprise networking, data centre, and managed services solutions that meet government standards - helping public institutions modernise their infrastructure while maintaining sovereignty, security, and continuous service delivery.',
            image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=900&auto=format&fit=crop&q=80',
            capabilities: ['Sovereign network architecture', 'Government security compliance', 'Data centre modernisation', 'Managed service continuity'],
            accent: '#ef4444'
        },
        {
            id: '07',
            title: 'Telecommunications',
            shortDesc: 'Carrier-grade infrastructure for service providers',
            description: 'Telecommunications providers need highly available, scalable infrastructure that delivers carrier-grade performance across complex multi-site environments. Solaris supports telcos and service providers with network design, managed infrastructure services, and data centre solutions that underpin service delivery, reduce operational costs, and enable rapid service expansion.',
            image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=900&auto=format&fit=crop&q=80',
            capabilities: ['Carrier-grade networking', 'Data centre colocation support', 'Network operations management', 'Scalable infrastructure design'],
            accent: '#8b5cf6'
        },
        {
            id: '08',
            title: 'Energy & Utilities',
            shortDesc: 'Mission-critical networks for uninterrupted operations',
            description: 'Energy and utility companies operate critical infrastructure where network downtime is never an option. Solaris designs and manages secure, redundant network environments that connect substations, control centres, and enterprise systems - ensuring operational continuity, regulatory compliance, and the resilience needed to support essential services at all times.',
            image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=900&auto=format&fit=crop&q=80',
            capabilities: ['SCADA & OT connectivity', 'Redundant network design', 'Critical infrastructure security', 'Remote site management'],
            accent: '#f97316'
        }
    ];

    const stats = [
        { value: '15+', label: 'Industries served across enterprise and mid-market segments' },
        { value: '200+', label: 'Enterprise deployments delivered across India and globally' },
        { value: '99.9%', label: 'Uptime SLA maintained across managed infrastructure environments' },
        { value: '24/7', label: 'Operations support available across all industry verticals' },
    ];

    return (
        <>
            {/* ── HERO ── */}
            <section style={{
                position: 'relative', width: '100%', minHeight: '520px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                backgroundImage: `url('https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1600&auto=format&fit=crop&q=80')`,
                backgroundSize: 'cover', backgroundPosition: 'center', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(8,16,40,0.78)' }} />
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.06) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, padding: '140px 15px 100px', textAlign: 'center' }}>
                    <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
                        <div style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            background: 'rgba(37,99,235,0.2)', border: '1px solid rgba(37,99,235,0.4)',
                            borderRadius: '50px', padding: '6px 18px', marginBottom: '28px'
                        }}>
                            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#60a5fa' }} />
                            <span style={{ color: '#60a5fa', fontWeight: 700, letterSpacing: '2.5px', textTransform: 'uppercase', fontSize: '0.72rem' }}>
                                Industries We Serve
                            </span>
                        </div>
                        <h1 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2.2rem, 5vw, 4rem)',
                            lineHeight: 1.15, margin: '0 auto 24px', maxWidth: '860px'
                        }}>
                            Deep Expertise Across{' '}
                            <span style={{
                                background: 'linear-gradient(135deg, #60a5fa, #38bdf8)',
                                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'
                            }}>
                                Every Vertical
                            </span>
                        </h1>
                        <p style={{
                            color: 'rgba(255,255,255,0.72)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '580px', margin: '0 auto 40px'
                        }}>
                            Solaris brings sector-specific knowledge and proven IT infrastructure expertise to organisations across some of the world's most demanding industries.
                        </p>
                        <a href="#industries-grid" style={{ textDecoration: 'none' }}>
                            <button style={{
                                display: 'inline-flex', alignItems: 'center',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                color: '#fff', border: 'none',
                                padding: '14px 8px 14px 28px',
                                borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                cursor: 'pointer', boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 12px 32px rgba(37,99,235,0.55)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                            >
                                <span style={{ marginRight: '16px' }}>Explore Industries</span>
                                <span style={{
                                    background: '#fff', borderRadius: '50%', width: '38px', height: '38px',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </button>
                        </a>
                    </motion.div>
                </div>
            </section>

            {/* ── STATS STRIP ── */}
            <section style={{ background: '#0f172a', padding: '0' }}>
                <div className="container">
                    <div className="row g-0">
                        {stats.map((s, i) => (
                            <div key={i} className="col-lg-3 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 16 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.1 }}
                                    viewport={{ once: true }}
                                    style={{
                                        padding: '40px 28px',
                                        borderRight: i < 3 ? '1px solid rgba(255,255,255,0.07)' : 'none',
                                        borderBottom: '1px solid rgba(255,255,255,0.07)'
                                    }}
                                >
                                    <div style={{
                                        fontSize: 'clamp(1.8rem, 3vw, 2.4rem)', fontWeight: 800,
                                        color: '#60a5fa', marginBottom: '10px', lineHeight: 1
                                    }}>{s.value}</div>
                                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.88rem', lineHeight: 1.65, margin: 0 }}>{s.label}</p>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── INDUSTRIES INTERACTIVE ── */}
            <section id="industries-grid" style={{ padding: '100px 0', background: '#fff' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>SECTOR EXPERTISE</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.8rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginBottom: '16px' }}>
                            Industries We Power
                        </h2>
                        <p style={{ color: '#64748b', fontSize: '1.05rem', maxWidth: '560px', margin: '0 auto', lineHeight: 1.75 }}>
                            From highly regulated sectors to fast-moving digital businesses, Solaris delivers infrastructure solutions tailored to the demands of each industry.
                        </p>
                    </div>

                    {/* Tab selector */}
                    <div style={{
                        display: 'flex', flexWrap: 'wrap', gap: '8px',
                        justifyContent: 'center', marginBottom: '56px'
                    }}>
                        {industries.map((ind, i) => (
                            <button
                                key={i}
                                onClick={() => setActiveIndustry(i)}
                                style={{
                                    padding: '10px 20px', borderRadius: '50px',
                                    border: activeIndustry === i ? `1.5px solid ${ind.accent}` : '1.5px solid #e2e8f0',
                                    background: activeIndustry === i ? ind.accent : '#fff',
                                    color: activeIndustry === i ? '#fff' : '#64748b',
                                    fontWeight: 600, fontSize: '0.88rem',
                                    cursor: 'pointer', transition: 'all 0.2s',
                                    whiteSpace: 'nowrap'
                                }}
                            >
                                {ind.title.split(' ')[0]}{ind.title.split(' ')[1] ? ' ' + ind.title.split(' ')[1] : ''}
                            </button>
                        ))}
                    </div>

                    {/* Active industry detail */}
                    <motion.div
                        key={activeIndustry}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="row align-items-center g-5"
                    >
                        <div className="col-lg-6">
                            <div style={{ borderRadius: '20px', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.12)' }}>
                                <img
                                    src={industries[activeIndustry].image}
                                    alt={industries[activeIndustry].title}
                                    style={{ width: '100%', height: '400px', objectFit: 'cover', display: 'block' }}
                                />
                            </div>
                        </div>
                        <div className="col-lg-6">
                            <div style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                background: industries[activeIndustry].accent + '15',
                                border: `1px solid ${industries[activeIndustry].accent}30`,
                                borderRadius: '50px', padding: '5px 16px', marginBottom: '20px'
                            }}>
                                <span style={{ color: industries[activeIndustry].accent, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                    {industries[activeIndustry].id} - Industry Focus
                                </span>
                            </div>
                            <h3 style={{ fontSize: 'clamp(1.6rem, 3vw, 2.2rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginBottom: '12px' }}>
                                {industries[activeIndustry].title}
                            </h3>
                            <p style={{ color: '#2563eb', fontWeight: 600, fontSize: '0.95rem', marginBottom: '20px' }}>
                                {industries[activeIndustry].shortDesc}
                            </p>
                            <p style={{ color: '#475569', fontSize: '1rem', lineHeight: 1.85, marginBottom: '32px' }}>
                                {industries[activeIndustry].description}
                            </p>
                            <div style={{ marginBottom: '36px' }}>
                                <p style={{ fontWeight: 700, color: '#0f172a', fontSize: '0.88rem', textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '16px' }}>Key Capabilities</p>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                    {industries[activeIndustry].capabilities.map((cap, ci) => (
                                        <span key={ci} style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                                            padding: '8px 16px', borderRadius: '8px',
                                            background: industries[activeIndustry].accent + '10',
                                            border: `1px solid ${industries[activeIndustry].accent}25`,
                                            color: '#0f172a', fontSize: '0.85rem', fontWeight: 500
                                        }}>
                                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                                                <path d="M5 13L9 17L19 7" stroke={industries[activeIndustry].accent} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            {cap}
                                        </span>
                                    ))}
                                </div>
                            </div>
                            <Link to="/contact" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '10px',
                                background: `linear-gradient(135deg, ${industries[activeIndustry].accent}, #0ea5e9)`,
                                color: '#fff', padding: '13px 28px', borderRadius: '8px',
                                fontWeight: 700, fontSize: '0.9rem', textDecoration: 'none',
                                boxShadow: `0 6px 20px ${industries[activeIndustry].accent}40`,
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                            >
                                Discuss Your Requirements
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* ── ALL INDUSTRIES GRID ── */}
            <section style={{ padding: '90px 0', background: '#f8fafc' }}>
                <div className="container">
                    <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                        <p style={{
                            color: '#2563eb', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '14px'
                        }}>AT A GLANCE</p>
                        <h2 style={{ fontSize: 'clamp(1.9rem, 3.5vw, 2.6rem)', fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>
                            All Industries We Serve
                        </h2>
                    </div>
                    <div className="row g-4">
                        {industries.map((ind, i) => (
                            <div key={i} className="col-lg-3 col-md-6">
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5, delay: i * 0.07 }}
                                    viewport={{ once: true }}
                                    onClick={() => { setActiveIndustry(i); document.getElementById('industries-grid').scrollIntoView({ behavior: 'smooth' }); }}
                                    style={{
                                        borderRadius: '16px', overflow: 'hidden',
                                        border: '1px solid #e2e8f0', background: '#fff',
                                        cursor: 'pointer', transition: 'all 0.3s',
                                        boxShadow: '0 2px 12px rgba(0,0,0,0.04)'
                                    }}
                                    whileHover={{ y: -6, boxShadow: '0 16px 40px rgba(0,0,0,0.1)' }}
                                >
                                    <div style={{ position: 'relative', height: '160px', overflow: 'hidden' }}>
                                        <img
                                            src={ind.image}
                                            alt={ind.title}
                                            style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                                        />
                                        <div style={{
                                            position: 'absolute', inset: 0,
                                            background: 'linear-gradient(to bottom, transparent 30%, rgba(0,0,0,0.55) 100%)'
                                        }} />
                                        <div style={{
                                            position: 'absolute', top: '12px', left: '12px',
                                            background: ind.accent, color: '#fff',
                                            fontSize: '0.68rem', fontWeight: 700,
                                            padding: '3px 10px', borderRadius: '50px', letterSpacing: '1px'
                                        }}>{ind.id}</div>
                                    </div>
                                    <div style={{ padding: '20px 20px 22px' }}>
                                        <h4 style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a', marginBottom: '8px', lineHeight: 1.35 }}>{ind.title}</h4>
                                        <p style={{ color: '#64748b', fontSize: '0.82rem', lineHeight: 1.65, marginBottom: 0 }}>{ind.shortDesc}</p>
                                    </div>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── DARK CTA ── */}
            <section style={{ padding: '100px 0', background: '#0b1120', position: 'relative', overflow: 'hidden' }}>
                <div style={{
                    position: 'absolute', inset: 0,
                    backgroundImage: 'linear-gradient(rgba(37,99,235,0.07) 1px, transparent 1px), linear-gradient(90deg, rgba(37,99,235,0.07) 1px, transparent 1px)',
                    backgroundSize: '60px 60px', pointerEvents: 'none'
                }} />
                <div style={{
                    position: 'absolute', top: '-100px', right: '-100px', width: '500px', height: '500px',
                    borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)',
                    pointerEvents: 'none'
                }} />
                <div className="container" style={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7 }}
                        viewport={{ once: true }}
                    >
                        <p style={{
                            color: '#60a5fa', fontWeight: 700, letterSpacing: '3px',
                            textTransform: 'uppercase', fontSize: '0.78rem', marginBottom: '20px'
                        }}>WORK WITH US</p>
                        <h2 style={{
                            color: '#fff', fontWeight: 800,
                            fontSize: 'clamp(2rem, 4vw, 3.2rem)',
                            lineHeight: 1.2, marginBottom: '20px', maxWidth: '720px', margin: '0 auto 20px'
                        }}>
                            Don't See Your Industry?<br />We'd Still Love to Help.
                        </h2>
                        <p style={{
                            color: 'rgba(255,255,255,0.55)', fontSize: '1.05rem',
                            lineHeight: 1.8, maxWidth: '520px', margin: '0 auto 40px'
                        }}>
                            Our infrastructure expertise translates across sectors. Tell us about your environment and we'll design a solution that works for your specific needs.
                        </p>
                        <Link to="/contact" style={{ textDecoration: 'none' }}>
                            <button style={{
                                display: 'inline-flex', alignItems: 'center',
                                background: 'linear-gradient(135deg,#2563eb,#0ea5e9)',
                                color: '#fff', border: 'none',
                                padding: '16px 10px 16px 32px',
                                borderRadius: '50px', fontWeight: 700, fontSize: '1rem',
                                cursor: 'pointer', boxShadow: '0 8px 24px rgba(37,99,235,0.4)',
                                transition: 'transform 0.2s, box-shadow 0.2s'
                            }}
                                onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 14px 32px rgba(37,99,235,0.55)'; }}
                                onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 8px 24px rgba(37,99,235,0.4)'; }}
                            >
                                <span style={{ marginRight: '16px' }}>Get In Touch</span>
                                <span style={{
                                    background: '#fff', borderRadius: '50%', width: '40px', height: '40px',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                                }}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="#0f172a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </span>
                            </button>
                        </Link>
                    </motion.div>
                </div>
            </section>

            <ContactSection />
        </>
    );
};

export default Industries;
